import { useEffect, useState } from "react";
import { useMyProviderDataClient } from "../../../../context/ApiContext";

export interface RatesResult {
  headers: string[];
  rows: string[];
  loading: boolean;
  error?: string;
}

export const useRates = (
  username?: string,
  role?: string,
  analystLevel?: string,
  region?: string,
  employeeStatus?: string,
  enabled: boolean = true
): RatesResult => {
  const client = useMyProviderDataClient();

  const [headers, setHeaders] = useState<string[]>([]);
  const [rows, setRows] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();

  useEffect(() => {
    if (!enabled || !username) return;

    const loadRates = async () => {
      try {
        setLoading(true);
        setError(undefined);

        // 1️⃣ Get current rate
        const resRate = await client.get("/EmployeeInformation/CurrentRate", {
          params: { username }
        });
        const currentRate: number = resRate.data?.currentRate ?? 0;

        // 2️⃣ Get region-serviced data
        const resRegion = await client.get("/EmployeeInformation/RegionServiced", {
          params: { username, region }
        });
        const regionData = resRegion.data;

        // 3️⃣ Calculate rates like old createRatesObject
        let standardRate = 0;
        switch (role) {
          case "Analyst":
            if (analystLevel === "BCBA") standardRate = regionData.bcbaStandardRate;
            else if (analystLevel === "BCaBA") standardRate = regionData.bCaBAStandardRate;
            break;
          case "Registered Behavior Technician":
            standardRate = regionData.rbtStandardRate;
            break;
          case "Assistant":
            standardRate = regionData.assistantStandardRate;
            break;
          case "Program Manager":
            standardRate = regionData.programManagerStandardRate;
            break;
          case "Student Analyst":
            standardRate = regionData.studentAnalystStandardRate;
            break;
        }

        let meetingRate = 0, indirectRate = 0, driveRate = 0, rateCap = 0;
        if (role === "Analyst") {
          indirectRate = regionData.meetingTimeAnalyst * 60;
          meetingRate = regionData.meetingTimeAnalyst * 60;
          driveRate = regionData.driveTimeAnalyst * 60;
        } else {
          indirectRate = regionData.meetingTimeAllOthers * 60;
          meetingRate = regionData.meetingTimeAllOthers * 60;
          driveRate = regionData.driveTimeAllOthers * 60;
        }

        // Admin Time: every non-exempt employee, any state and any role, from DriveTimeAllOthers
        const showAdminRate = employeeStatus?.toLowerCase() === "non-exempt";
        const adminRate = regionData.driveTimeAllOthers * 60;

        rateCap = standardRate * 1.25;

        let headersArr: string[] = [];
        let rowsArr: string[] = [];

        if (region === "British Columbia" || region === "Ontario") {
          rowsArr.push(`$${currentRate.toFixed(2)}/hr`);
          headersArr.push("Direct Rate");
        } else {
          headersArr.push("Drive Time");
          rowsArr.push(`$${driveRate.toFixed(2)}/hr`);

          if (showAdminRate) {
            headersArr.push("Admin Time");
            rowsArr.push(`$${adminRate.toFixed(2)}/hr`);
          }

          headersArr.push("Meeting & Training");
          rowsArr.push(`$${meetingRate.toFixed(2)}/hr`);

          if (role === "Analyst" || role === "Program Manager") {
            headersArr.push("Indirect Time");
            rowsArr.push(`$${indirectRate.toFixed(2)}/hr`);
          }

          headersArr.push("Direct Rate");
          rowsArr.push(`$${currentRate.toFixed(2)}/hr`);

          if (role === "Registered Behavior Technician" || role === "Assistant") {
            headersArr.push("Rate Cap");
            rowsArr.push(`$${rateCap.toFixed(2)}/hr`);
          }
        }

        setHeaders(headersArr);
        setRows(rowsArr);
      } catch (err) {
        console.error(err);
        setError("Failed to load rate data.");
      } finally {
        setLoading(false);
      }
    };

    loadRates();
  }, [username, role, analystLevel, region, employeeStatus, enabled, client]);

  return { headers, rows, loading, error };
};